// Spec data for the screens: one index over the synced spec tables (rebuilt only when one of them changes and
// shared by every component that asks), statuses, uploads with progress, and pasted images.
import { useSyncExternalStore } from "react";
import type { FileVersion, Spec, SpecFile, SpecRevision } from "../../../../shared/db.gen.ts";
import { groupBy } from "../../../../shared/group.ts";
import {
  byRev,
  draftFiles,
  draftStamp,
  type FileRef,
  nextRev,
  pendingRelease,
  type SpecFileKind,
  type SpecStatus,
  specsByItem,
} from "../../../../shared/specs.ts";
import { dayText } from "../../lib/format.ts";
import { mutate } from "../../lib/mutate.ts";
import { useRows } from "../../lib/sync.ts";

export type SpecIndex = {
  /** Each item's spec: its own, or for a Shopify variant its product's (migration 059: one spec per product). */
  specByItem: Map<string, Spec>;
  /** Every spec once, with the item it's shown under (a product's: its first variant by SKU). */
  specs: Spec[];
  /** A Shopify product's spec, by the product's id. */
  specByProduct: Map<string, Spec>;
  itemOfSpec: Map<string, string>;
  filesBySpec: Map<string, SpecFile[]>;
  versionsByFile: Map<string, FileVersion[]>;
  versionById: Map<string, FileVersion>;
  revisionsBySpec: Map<string, SpecRevision[]>;
  revisionById: Map<string, SpecRevision>;
  /** Each revision's files, with the labels it froze at release. */
  filesByRevision: Map<string, FileRef[]>;
  /** The revisions (letters, oldest first) each file version was released in. */
  revsByVersion: Map<string, string[]>;
  userName: Map<string, string>;
};

let last: { inputs: unknown[]; index: SpecIndex } | null = null;

export function useSpecIndex(): SpecIndex {
  const specs = useRows("spec");
  const files = useRows("spec_file");
  const versions = useRows("file_version");
  const revisions = useRows("spec_revision");
  const revFiles = useRows("spec_revision_file");
  const users = useRows("app_user");
  const items = useRows("item");
  const inputs = [specs, files, versions, revisions, revFiles, users, items];
  if (!last || last.inputs.some((x, i) => x !== inputs[i])) {
    const versionById = new Map(versions.map((v) => [v.id, v]));
    const revisionById = new Map(revisions.map((r) => [r.id, r]));
    const filesByRevision = new Map<string, FileRef[]>();
    const revsByVersion = new Map<string, string[]>();
    for (const rf of revFiles) {
      const v = versionById.get(rf.file_version_id);
      const r = revisionById.get(rf.spec_revision_id);
      if (!v || !r) continue;
      const ref = { spec_file_id: v.spec_file_id, file_version_id: v.id, sha256: v.sha256, name: rf.name, kind: rf.kind, position: rf.position };
      filesByRevision.set(r.id, [...(filesByRevision.get(r.id) ?? []), ref]);
      revsByVersion.set(
        v.id,
        [...(revsByVersion.get(v.id) ?? []), r.rev].sort((a, b) => byRev({ rev: a }, { rev: b })),
      );
    }
    for (const list of filesByRevision.values()) list.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
    const filesBySpec = groupBy(files, (f) => f.spec_id);
    for (const list of filesBySpec.values()) list.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
    const versionsByFile = groupBy(versions, (v) => v.spec_file_id);
    for (const list of versionsByFile.values()) list.sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at));
    const revisionsBySpec = groupBy(revisions, (r) => r.spec_id);
    for (const list of revisionsBySpec.values()) list.sort(byRev);
    // One spec per product (migration 059): every variant of a product goes by its product's spec, shown under its
    // first variant by SKU.
    const specByItem = specsByItem(specs, items);
    const specByProduct = new Map(specs.flatMap((s) => (s.shopify_product_id ? [[s.shopify_product_id, s] as const] : [])));
    const itemOfSpec = new Map(specs.flatMap((s) => (s.item_id ? [[s.id, s.item_id] as const] : [])));
    if (specByProduct.size)
      for (const i of [...items].sort((a, b) => a.sku.localeCompare(b.sku, undefined, { numeric: true }))) {
        const s = specByItem.get(i.id);
        if (s?.shopify_product_id && !i.archived && !itemOfSpec.has(s.id)) itemOfSpec.set(s.id, i.id);
      }
    last = {
      inputs,
      index: {
        specByItem,
        specByProduct,
        specs: [...specs],
        itemOfSpec,
        filesBySpec,
        versionsByFile,
        versionById,
        revisionsBySpec,
        revisionById,
        filesByRevision,
        revsByVersion,
        userName: new Map(users.map((u) => [u.id, u.name || u.email])),
      },
    };
  }
  return last.index;
}

/** What releasing the spec's draft now would change (see pendingRelease in shared/specs.ts). */
export function pendingOf(ix: SpecIndex, spec: Spec) {
  const current = spec.current_revision_id ? ix.revisionById.get(spec.current_revision_id) : undefined;
  return pendingRelease(spec, draftRefs(ix, spec), current ? { ...current, files: ix.filesByRevision.get(current.id) ?? [] } : null);
}

export function statusOf(ix: SpecIndex, spec: Spec | undefined): SpecStatus {
  if (!spec) return { kind: "none" };
  const current = spec.current_revision_id ? ix.revisionById.get(spec.current_revision_id) : undefined;
  if (!current) return { kind: "draft" };
  return pendingOf(ix, spec).any ? { kind: "ahead", rev: current.rev } : { kind: "released", rev: current.rev };
}

/** The draft's files as the next release would freeze them. */
export const draftRefs = (ix: SpecIndex, spec: Spec): FileRef[] => draftFiles(ix.filesBySpec.get(spec.id) ?? [], (id) => ix.versionById.get(id)?.sha256);

/**
 * What a release dialog shows and then sends: the next letter, what would change against the current revision, the
 * in-draft files with nothing uploaded, and the draft's stamp (draftStamp in shared/specs.ts), which the server
 * checks so only the draft shown is released. `text` is unsaved text the dialog saves before releasing.
 */
export function releasePreview(ix: SpecIndex, spec: Spec, text?: { draft_md: string; draft_md_zh: string }) {
  const body = text ?? spec;
  const draft = draftRefs(ix, spec);
  const current = spec.current_revision_id ? ix.revisionById.get(spec.current_revision_id) : undefined;
  return {
    rev: nextRev((ix.revisionsBySpec.get(spec.id) ?? []).map((r) => r.rev)),
    current: current?.rev ?? null,
    pending: pendingRelease(body, draft, current ? { ...current, files: ix.filesByRevision.get(current.id) ?? [] } : null),
    missing: (ix.filesBySpec.get(spec.id) ?? []).filter((f) => f.in_draft && !f.current_version_id).map((f) => f.name),
    stamp: draftStamp(body, draft),
  };
}
export type ReleasePreview = ReturnType<typeof releasePreview>;

export async function startSpec(itemId: string) {
  const id = crypto.randomUUID();
  // Empty: the old template's sections were mostly left blank, clutter on what vendors read (Mitch, 2026-11-04)
  return (await mutate("spec.create", { id, item_id: itemId, draft_md: "" })) ? id : null;
}

// A new spec or slot shows at once but isn't on the server until its mutation returns: what's dropped meanwhile (a
// second quick drop, or a part group's files going up) waits for it rather than making another.
const making = new Map<string, Promise<string | null>>();
const once = (key: string, make: () => Promise<string | null>) => {
  let p = making.get(key);
  if (!p) {
    p = make();
    making.set(key, p);
    p.finally(() => making.delete(key));
  }
  return p;
};

/** The part's spec, started if it hasn't one (null when that was refused). */
export function specFor(ix: SpecIndex, itemId: string): Promise<string | null> {
  const spec = ix.specByItem.get(itemId);
  return making.get(`spec:${itemId}`) ?? (spec ? Promise.resolve(spec.id) : once(`spec:${itemId}`, () => startSpec(itemId)));
}

/** The slot a needed file goes in: one of its kind in the draft with an upload, else in the draft, else left out. */
export function needSlot(slots: readonly SpecFile[], kinds: readonly string[]): SpecFile | undefined {
  const fits = (s: SpecFile) => kinds.includes(s.kind);
  return slots.find((s) => s.in_draft && fits(s) && s.current_version_id) ?? slots.find((s) => s.in_draft && fits(s)) ?? slots.find(fits);
}

/**
 * Uploads a file a part needs (a print file) as the newest version of its slot: the spec started and the slot (named
 * after the need) made if they aren't there, a slot left out of the draft put back in. Null when a spec or slot
 * couldn't be made (the refusal is shown); throws when the upload fails.
 */
export async function uploadNeed(
  ix: SpecIndex,
  itemId: string,
  need: { label: string; kinds: readonly SpecFileKind[] },
  file: File,
): Promise<UploadResult | null> {
  const sid = await specFor(ix, itemId);
  if (!sid) return null;
  const slots = ix.filesBySpec.get(sid) ?? [];
  const slot = needSlot(slots, need.kinds);
  const key = `slot:${itemId}:${need.label}`;
  const slotId =
    (await making.get(key)) ??
    slot?.id ??
    (await once(key, async () => {
      const id = crypto.randomUUID();
      const position = Math.max(-1, ...slots.map((s) => s.position)) + 1;
      return (await mutate("spec_file.create", { id, spec_id: sid, kind: need.kinds[0], name: need.label, in_draft: true, position })) ? id : null;
    }));
  if (!slotId) return null;
  if (slot?.id === slotId && !slot.in_draft) await mutate("spec_file.update", { id: slotId, patch: { in_draft: true } }); // a file left out goes back in
  return uploadVersion(slotId, file);
}

export const downloadUrl = (versionId: string, inline = false) => `/api/file-versions/${versionId}/download${inline ? "?inline=1" : ""}`;
export const viewable = (contentType: string) => /^(application\/pdf|image\/(png|jpeg|webp|gif)|text\/plain)$/.test(contentType);

export function bytes(n: number) {
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB"];
  let v = n / 1024;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[u]}`;
}

export const day = dayText;

// Uploads in progress, by spec file (a new version) or by a new file's id: kept outside components so they
// survive tab switches and closing the panel.
export type Upload = { name: string; loaded: number; total: number };
let uploads = new Map<string, Upload>();
const listeners = new Set<() => void>();
const setUpload = (key: string, u: Upload | null) => {
  uploads = new Map(uploads);
  u ? uploads.set(key, u) : uploads.delete(key);
  for (const fn of listeners) fn();
};
export const useUploads = () =>
  useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => uploads,
  );

/** KiCad projects --- what KiCad made from a project: each file, the board's warnings, KiCad's version. */
export type KicadMade = { kicad: string; warnings: string[]; files: { name: string; filename: string; added: boolean; unchanged: boolean }[] };
/** An upload's answer; a KiCad project's also has what KiCad made (null: nothing, the project unchanged or refused). */
export type UploadResult = { id: string; unchanged: boolean; made?: KicadMade | null; kicadError?: string };

/**
 * Streams a file to the server as a new version of a spec file, reporting progress (fetch can't). `unchanged`: the
 * bytes are the current version's, so the server kept that one. `kicad`: a KiCad project, which KiCad then makes files from.
 */
export function uploadVersion(specFileId: string, file: File, note = "", kicad = false): Promise<UploadResult> {
  setUpload(specFileId, { name: file.name, loaded: 0, total: file.size });
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    // KiCad projects --- a KiCad project goes to the route that has KiCad make its files once it's in.
    xhr.open("POST", `/api/spec-files/${specFileId}/${kicad ? "kicad" : "versions"}`);
    xhr.setRequestHeader("x-requested-with", "hhl-ops");
    xhr.setRequestHeader("content-type", file.type || "application/octet-stream");
    xhr.setRequestHeader("x-filename", encodeURIComponent(file.name));
    if (note) xhr.setRequestHeader("x-note", encodeURIComponent(note));
    xhr.upload.onprogress = (e) => {
      const total = e.total || file.size;
      const name = kicad && e.loaded >= total ? `${file.name}: KiCad is making the files…` : file.name;
      setUpload(specFileId, { name, loaded: e.loaded, total });
    };
    const done = () => setUpload(specFileId, null);
    xhr.onload = () => {
      done();
      let body: Partial<UploadResult> & { error?: string } = {};
      try {
        body = JSON.parse(xhr.responseText);
      } catch {}
      if (xhr.status >= 200 && xhr.status < 300 && body.id)
        resolve({ id: body.id, unchanged: !!body.unchanged, made: body.made ?? null, kicadError: body.kicadError });
      else reject(new Error(body.error ?? `Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => {
      done();
      reject(new Error(`Couldn't upload ${file.name}: the connection dropped`));
    };
    xhr.send(file);
  });
}

// Pasted and dropped images: big ones are resized (longest side 2400 px, WebP) so specs load fast; small ones and
// GIFs (often animated) go as they are.
const MAX_SIDE = 2400;
export async function uploadSpecImage(file: File): Promise<string> {
  let blob: Blob = file;
  if (file.type !== "image/gif") {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    if (scale < 1 || file.size > 1_500_000 || !["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      const canvas = new OffscreenCanvas(Math.round(bitmap.width * scale), Math.round(bitmap.height * scale));
      canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      blob = await canvas.convertToBlob({ type: "image/webp", quality: 0.88 });
    }
  }
  const res = await fetch("/api/spec-images", {
    method: "POST",
    headers: { "content-type": blob.type, "x-requested-with": "hhl-ops" },
    body: blob,
  });
  const body = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
  if (!res.ok || !body.url) throw new Error(body.error ?? "Image upload failed");
  return body.url;
}
